import {
  hasAuthoritativeSender,
  normalizeSessionListSenders,
  sessionListSenderName,
  truncateSessionTitle,
} from "./agentSessionsPanelUtils";

// Status string for a freshly created session that is still in flight. Mirrors
// the backend lifecycle value; kept local so this optimistic-list island stays
// self-contained.
const STATUS_IN_PROGRESS = "IN_PROGRESS";

// Merge a derived patch into an existing row, skipping empty values so good
// metadata (a real title, a denormalized sender) is never clobbered by an
// undefined/null/"" field from a partial detail object.
function mergeRowFields(existing, patch) {
  if (!existing || !patch) return existing;
  const next = { ...existing };
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined && value !== null && value !== "") {
      next[key] = value;
    }
  }
  return next;
}

// Build the optimistic row shown in the session list the instant a chat is
// started, before the server returns the persisted session.
export function buildOptimisticSessionListRow({
  chatSessionId,
  message,
  senderDisplayName,
}) {
  const now = new Date().toISOString();
  const sender = senderDisplayName || "You";
  return {
    chatSessionId,
    title: truncateSessionTitle(message, "New session"),
    senderDisplayName: sender,
    senderName: sender,
    status: STATUS_IN_PROGRESS,
    createdAt: now,
    updatedAt: now,
    messageCount: 1,
    _optimisticListRow: true,
  };
}

// Insert or merge a row into the session list, keeping newest-first ordering and
// replacing any existing row with the same chatSessionId.
export function upsertSessionListRow(sessions, row) {
  const list = Array.isArray(sessions) ? sessions : [];
  const idx = list.findIndex((s) => s?.chatSessionId === row.chatSessionId);
  if (idx >= 0) {
    const next = [...list];
    next[idx] = { ...next[idx], ...row };
    return next;
  }
  return [row, ...list];
}

// Reconcile a freshly fetched server session list against the current optimistic
// rows: prefer an authoritative server sender, otherwise keep the optimistic
// sender until the server denormalizes one, and preserve still-pending optimistic
// rows the server hasn't acknowledged yet.
export function mergeFetchedSessionList(currentSessions, serverSessions) {
  const current = Array.isArray(currentSessions) ? currentSessions : [];
  const server = Array.isArray(serverSessions) ? serverSessions : [];
  const optimisticById = new Map();
  for (const row of current) {
    if (row?._optimisticListRow && row.chatSessionId) {
      optimisticById.set(row.chatSessionId, row);
    }
  }

  const merged = server.map((serverRow) => {
    const optimistic = optimisticById.get(serverRow.chatSessionId);
    optimisticById.delete(serverRow.chatSessionId);
    if (!optimistic) return serverRow;

    const serverSender = sessionListSenderName(serverRow);
    if (hasAuthoritativeSender(serverSender)) {
      return { ...serverRow, _optimisticListRow: false };
    }

    const optimisticSender = sessionListSenderName(optimistic);
    return {
      ...serverRow,
      title: serverRow.title || optimistic.title,
      senderDisplayName: optimisticSender || serverSender || undefined,
      senderName: optimisticSender || serverSender || undefined,
      _optimisticListRow: !optimisticSender,
    };
  });

  for (const remaining of optimisticById.values()) {
    merged.unshift(remaining);
  }
  return merged;
}

// During a just-started chat, fill the sender on the brand-new server row that
// corresponds to the session we just created, so the sidebar shows the user
// immediately instead of the "System" placeholder. `localUserDisplayName` is the
// caller-resolved fallback (this module stays free of store dependencies).
export function enrichSessionsFromStartingChatContext(
  sessions,
  {
    serverSessions,
    priorSessions,
    startingChatContext,
    sessionDetail,
    localUserDisplayName,
  }
) {
  if (!startingChatContext) return sessions;

  const list = Array.isArray(sessions) ? sessions : [];
  const priorIds = new Set(
    (Array.isArray(priorSessions) ? priorSessions : [])
      .map((row) => row?.chatSessionId)
      .filter(Boolean)
  );
  const server = Array.isArray(serverSessions) ? serverSessions : [];
  const brandNewRows = server.filter(
    (row) => row?.chatSessionId && !priorIds.has(row.chatSessionId)
  );

  let targetId = sessionDetail?.chatSessionId || null;
  if (!targetId) {
    if (brandNewRows.length === 1) {
      targetId = brandNewRows[0].chatSessionId;
    } else if (brandNewRows.length > 0) {
      const expectedTitle = truncateSessionTitle(startingChatContext.message);
      const matched = brandNewRows.find((row) => {
        const rowTitle = String(row.title || "").trim();
        return (
          rowTitle === expectedTitle ||
          rowTitle === String(startingChatContext.message || "").trim()
        );
      });
      targetId = matched?.chatSessionId || null;
    }
  }

  if (!targetId) return list;

  const idx = list.findIndex((row) => row.chatSessionId === targetId);
  if (idx < 0) return list;

  const row = list[idx];
  const serverSender = sessionListSenderName(row);
  if (hasAuthoritativeSender(serverSender)) return list;

  const sender =
    startingChatContext.senderDisplayName || localUserDisplayName || "You";
  const next = [...list];
  next[idx] = {
    ...row,
    title: row.title || truncateSessionTitle(startingChatContext.message),
    senderDisplayName: sender,
    senderName: sender,
    _optimisticListRow: false,
  };
  return next;
}

// Derive the session-list row fields (title, sender, status, counts) from an
// open session's detail object. Title falls back to the first visible user
// message when the detail itself carries none.
export function deriveSessionListFieldsFromDetail(detail) {
  if (!detail?.chatSessionId) return null;
  const messages = Array.isArray(detail.messages) ? detail.messages : [];
  const firstVisibleUser = messages.find(
    (msg) =>
      msg?.role === "user" &&
      msg?.visibility !== "INTERNAL" &&
      String(msg?.content || "").trim()
  );
  const title =
    String(detail.title || "").trim() ||
    truncateSessionTitle(firstVisibleUser?.content);
  const senderDisplayName =
    detail.senderDisplayName ||
    detail.firstSenderDisplayName ||
    firstVisibleUser?.senderDisplayName ||
    null;
  const senderName =
    detail.senderName ||
    detail.firstSenderName ||
    firstVisibleUser?.senderName ||
    null;

  return {
    title: title || undefined,
    senderDisplayName: senderDisplayName || undefined,
    senderName: senderName || undefined,
    status: detail.status,
    statusReason: detail.statusReason || null,
    iterationsUsed: detail.iterationsUsed,
    updatedAt: detail.updatedAt,
    messageCount:
      detail.messageCount ??
      (messages.length > 0 ? messages.length : undefined),
  };
}

// Patch an open session's detail back into its existing list row. The open
// session is always already present in the list, so a missing row is a no-op
// (no fabricated insert). `clearOptimistic` drops the hydrating flag once the
// row is backed by loaded detail (used after terminal/explicit refreshes).
export function applySessionDetailToList(
  sessions,
  detail,
  { clearOptimistic = false } = {}
) {
  if (!detail?.chatSessionId) return sessions;
  const list = Array.isArray(sessions) ? sessions : [];
  const idx = list.findIndex((s) => s?.chatSessionId === detail.chatSessionId);
  if (idx < 0) return list;

  const patch = deriveSessionListFieldsFromDetail(detail);
  if (!patch) return list;

  const next = [...list];
  next[idx] = mergeRowFields(next[idx], patch);
  if (clearOptimistic) {
    next[idx] = { ...next[idx], _optimisticListRow: false };
  }
  return next;
}

// Full reconciliation for a freshly fetched session page: merge against prior
// optimistic rows, enrich the just-started session's sender, then normalize all
// senders. Single entry point so the store stays thin.
export function reconcileFetchedSessionList({
  priorSessions,
  serverSessions,
  agentId,
  startingChatContext,
  sessionDetail,
  localUserDisplayName,
}) {
  let sessions = mergeFetchedSessionList(priorSessions, serverSessions);
  if (startingChatContext?.agentId === agentId) {
    sessions = enrichSessionsFromStartingChatContext(sessions, {
      serverSessions,
      priorSessions,
      startingChatContext,
      sessionDetail,
      localUserDisplayName,
    });
  }
  return normalizeSessionListSenders(sessions, localUserDisplayName);
}
